import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { UploadCloud } from "lucide-react";
import { ApiError } from "../../api/client";
import {
  fileSha256,
  findDuplicate,
  getStatus,
  uploadBook,
  type Book,
  type EngineStatus,
} from "../../api/pqx";
import { bookStateLabel } from "./bookStates";
import { fetchSubjects } from "../../api/worker";

type Subject = Awaited<ReturnType<typeof fetchSubjects>>["subjects"][number];

const MAX_BYTES = 500 * 1024 * 1024;
const FALLBACK_ACCEPTS = [".pdf", ".png", ".jpg", ".jpeg", ".docx"];

/**
 * Upload a past-questions book for extraction.
 *
 * The subject matters to the engine: maths, physics and chemistry are read as
 * LaTeX with worked solutions, and the subject's existing topics are used for
 * tagging. A page range runs a cheap trial before a whole book is spent.
 */
export function PqxUpload() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<EngineStatus | null>(null);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [subjectId, setSubjectId] = useState("");
  const [examType, setExamType] = useState<"jamb" | "waec">("jamb");
  const [pages, setPages] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The book this file already is, when someone has uploaded it before. */
  const [duplicate, setDuplicate] = useState<Pick<
    Book,
    "book_id" | "filename" | "state" | "reviewer_name"
  > | null>(null);

  useEffect(() => {
    getStatus()
      .then(setStatus)
      .catch(() => setStatus({ configured: false, mock: false, ready: false, accepts: [] }));
    fetchSubjects()
      .then((r) => setSubjects(r.subjects))
      .catch(() => setSubjects([]));
  }, []);

  const accepts = status?.accepts.length ? status.accepts : FALLBACK_ACCEPTS;

  const choose = (picked: File | undefined) => {
    setError(null);
    setDuplicate(null);
    if (!picked) return;
    const suffix = picked.name.slice(picked.name.lastIndexOf(".")).toLowerCase();
    if (!accepts.includes(suffix)) {
      setError(`This file type can't be read. Use ${accepts.join(", ")}.`);
      return;
    }
    if (picked.size > MAX_BYTES) {
      setError("This file is larger than 500 MB.");
      return;
    }
    setFile(picked);
  };

  const start = async () => {
    if (!file || !subjectId) return;
    setError(null);
    setDuplicate(null);
    setChecking(true);
    let sha256: string | undefined;
    try {
      // Asked first, so a duplicate is refused in a second instead of after the
      // whole file has been sent. The backend checks the real bytes again.
      sha256 = await fileSha256(file);
      const known = (await findDuplicate(sha256)).duplicate;
      if (known) {
        setDuplicate(known);
        return;
      }
    } catch {
      // Hashing or the check failed: upload anyway, the backend still refuses
      // a duplicate once it has the bytes.
    } finally {
      setChecking(false);
    }
    setProgress(0);
    try {
      const book = await uploadBook(
        {
          file,
          subjectId,
          examType,
          ...(pages.trim() ? { pages: pages.trim() } : {}),
          ...(sha256 ? { sha256 } : {}),
        },
        setProgress,
      );
      navigate(`/books/${book.book_id}`);
    } catch (e) {
      setProgress(null);
      if (e instanceof ApiError && e.code === "duplicate_book" && e.details.book_id) {
        setDuplicate(e.details as unknown as NonNullable<typeof duplicate>);
        return;
      }
      setError(e instanceof Error ? e.message : "The upload failed. Try again.");
    }
  };

  const busy = progress !== null || checking;
  const unavailable = status !== null && !status.ready;
  const field =
    "w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-[14px] text-text outline-none focus:border-accent";

  return (
    <div className="flex flex-col gap-5">
      <p className="text-[13px] text-text-muted">
        Upload a scanned past-questions book. The engine extracts every question, its answer and any
        diagrams; they arrive in the question bank as <strong>under review</strong> for you to check
        and publish. A whole book takes tens of minutes.
      </p>

      {unavailable ? (
        <p role="alert" className="rounded-lg bg-warning/10 px-4 py-3 text-[13px] text-warning">
          {status?.configured
            ? `The extraction engine isn't ready${status.error ? `: ${status.error}` : ""}. Uploads will fail until it is.`
            : "The extraction engine isn't connected on this server yet."}
        </p>
      ) : null}
      {status?.mock ? (
        <p className="rounded-lg bg-surface-high px-4 py-2 text-[12px] text-text-muted">
          Local mock engine: every upload returns the same sample questions.
        </p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-3">
        <label className="block">
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
            Subject
          </span>
          <select
            value={subjectId}
            onChange={(e) => setSubjectId(e.target.value)}
            className={field}
          >
            <option value="">Choose…</option>
            {subjects.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.exam_type ? ` (${s.exam_type})` : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
            Exam
          </span>
          <select
            value={examType}
            onChange={(e) => setExamType(e.target.value as "jamb" | "waec")}
            className={field}
          >
            <option value="jamb">JAMB</option>
            <option value="waec">WAEC</option>
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
            Pages (optional)
          </span>
          <input
            value={pages}
            onChange={(e) => setPages(e.target.value)}
            placeholder="e.g. 1-4 for a trial"
            className={field}
          />
        </label>
      </div>

      <label
        htmlFor="pqx-file"
        className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border p-8 text-center transition-colors hover:border-accent"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          choose(e.dataTransfer.files[0]);
        }}
      >
        <UploadCloud size={22} className="text-text-muted" aria-hidden="true" />
        <span className="text-[14px] font-semibold">
          {file ? file.name : "Drop the book here, or click to choose"}
        </span>
        <span className="text-[12px] text-text-muted">
          {file
            ? `${(file.size / 1024 / 1024).toFixed(1)} MB`
            : `PDF works best. Also ${accepts.filter((a) => a !== ".pdf").join(", ")}. Up to 500 MB.`}
        </span>
        <input
          id="pqx-file"
          type="file"
          accept={accepts.join(",")}
          className="sr-only"
          onChange={(e) => choose(e.target.files?.[0])}
        />
      </label>

      {progress !== null ? (
        <div className="h-1.5 w-full overflow-hidden rounded bg-surface-high" aria-hidden="true">
          <div
            className="h-full bg-accent transition-[width]"
            style={{ width: `${progress * 100}%` }}
          />
        </div>
      ) : null}

      {error ? (
        <p role="alert" className="text-[13px] text-negative">
          {error}
        </p>
      ) : null}

      {duplicate ? (
        <div role="alert" className="rounded-lg bg-warning/10 px-4 py-3 text-[13px]">
          <p className="font-semibold text-warning">This book has already been uploaded</p>
          <p className="mt-1 text-text-muted">
            {duplicate.reviewer_name ? `${duplicate.reviewer_name} uploaded it` : "It was uploaded"}{" "}
            as <strong>{duplicate.filename}</strong> (
            {bookStateLabel(duplicate.state as Book["state"])}
            ). The same file can't be extracted twice. If that one failed or was removed, you can
            upload it again.
          </p>
          <Link
            to={`/books/${duplicate.book_id}`}
            className="mt-2 inline-block font-semibold text-accent-light"
          >
            Open the existing book
          </Link>
        </div>
      ) : null}

      <button
        type="button"
        disabled={!file || !subjectId || busy}
        onClick={() => void start()}
        className="w-full rounded-lg bg-accent px-4 py-2.5 text-[13px] font-semibold text-bg disabled:opacity-50 sm:w-auto sm:self-start"
      >
        {checking
          ? "Checking the file…"
          : progress !== null
            ? `Uploading… ${Math.round(progress * 100)}%`
            : "Upload and extract"}
      </button>
    </div>
  );
}
